import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2, Clock3, RotateCcw, XCircle, Zap } from 'lucide-react'
import { api, type Approval, type ApprovalStatus } from '@/lib/api'
import { cn, formatRelative } from '@/lib/utils'

type Tab = 'pending' | 'approved' | 'revision_requested' | 'rejected'

const TABS: Array<{ key: Tab; label: string; icon: typeof Clock3 }> = [
  { key: 'pending',             label: 'Pending',           icon: Clock3 },
  { key: 'approved',            label: 'Approved',          icon: CheckCircle2 },
  { key: 'revision_requested',  label: 'Revision',          icon: RotateCcw },
  { key: 'rejected',            label: 'Rejected',          icon: XCircle },
]

const STATUS_CHIP: Record<ApprovalStatus, string> = {
  pending:             'bg-status-review-bg text-status-review-fg',
  approved:            'bg-status-approved-bg text-status-approved-fg',
  revision_requested:  'bg-status-draft-bg text-status-draft-fg',
  rejected:            'bg-status-danger-bg text-status-danger-fg',
}

export function ApprovalsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('pending')
  const [revisionFor, setRevisionFor] = useState<Approval | null>(null)
  const [revisionNote, setRevisionNote] = useState('')
  const [searchParams] = useSearchParams()
  const highlightArticleId = searchParams.get('highlight')
  const highlightRef = useRef<HTMLElement | null>(null)

  const { data: approvals = [], isLoading } = useQuery({ queryKey: ['approvals'], queryFn: api.approvals })
  const { data: deliveries = [] } = useQuery({ queryKey: ['deliveries'], queryFn: api.deliveries })

  const approveMut = useMutation({
    mutationFn: (id: string) => api.approve(id),
    onSuccess: (res) => {
      toast.success('Approved', {
        description: `WordPress webhook fired · status ${res.status} · ${res.delivery_id.slice(0, 10)}`,
      })
      qc.invalidateQueries({ queryKey: ['approvals'] })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      qc.invalidateQueries({ queryKey: ['kpis'] })
      qc.invalidateQueries({ queryKey: ['articles'] })
    },
    onError: (err) => toast.error('Approval failed', { description: String(err) }),
  })

  const revisionMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.requestRevision(id, note),
    onSuccess: () => {
      toast.success('Revision requested')
      qc.invalidateQueries({ queryKey: ['approvals'] })
      setRevisionFor(null)
      setRevisionNote('')
    },
    onError: (err) => toast.error('Failed', { description: String(err) }),
  })

  useEffect(() => {
    if (!highlightArticleId || !approvals.length) return
    const target = approvals.find((a) => a.article_id === highlightArticleId)
    if (target && target.status !== tab) setTab(target.status as Tab)
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 50)
    return () => clearTimeout(t)
  }, [highlightArticleId, approvals, tab])

  const rows = approvals.filter((a) => a.status === tab)
  const counts: Record<Tab, number> = {
    pending: approvals.filter((a) => a.status === 'pending').length,
    approved: approvals.filter((a) => a.status === 'approved').length,
    revision_requested: approvals.filter((a) => a.status === 'revision_requested').length,
    rejected: approvals.filter((a) => a.status === 'rejected').length,
  }

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header>
        <h1 className="text-xl font-medium">Approvals</h1>
        <p className="text-xs text-ink-secondary mt-0.5">
          Approve pieces to fire the WordPress webhook. Requests for revision bounce the article back to draft.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3.5 min-w-0">
        <section className="border border-border rounded-md bg-surface-card">
          <div className="flex border-b border-border">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  'flex items-center gap-1.5 px-3.5 py-2.5 text-xs border-b-2 -mb-px transition-colors',
                  tab === key
                    ? 'border-brand-600 text-ink-primary font-medium'
                    : 'border-transparent text-ink-tertiary hover:text-ink-secondary',
                )}
              >
                <Icon size={14} />
                {label}
                <span className="text-[10px] text-ink-tertiary">({counts[key]})</span>
              </button>
            ))}
          </div>

          <div className="divide-y divide-border">
            {isLoading && <div className="p-4 text-xs text-ink-tertiary">Loading…</div>}
            {!isLoading && rows.length === 0 && (
              <div className="p-10 text-center text-sm text-ink-tertiary">
                {tab === 'pending' ? 'Inbox zero.' : 'Nothing here.'}
              </div>
            )}
            {rows.map((a) => (
              <article
                key={a.id}
                ref={a.article_id === highlightArticleId ? highlightRef : null}
                className={cn(
                  'px-4 py-3 flex gap-3 items-start transition-colors',
                  a.article_id === highlightArticleId && 'bg-brand-50/60 ring-1 ring-brand-400',
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded', STATUS_CHIP[a.status])}>
                      {a.status.replace('_', ' ')}
                    </span>
                    <span className="text-[11px] text-ink-tertiary">{a.type}</span>
                    {a.agent_name && (
                      <span className="flex items-center gap-1 text-[11px] text-brand-600">
                        <Zap size={11} /> {a.agent_name}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium mt-1 leading-snug">{a.article_title}</div>
                  <div className="text-xs text-ink-secondary mt-0.5">
                    {a.writer_name ?? a.agent_name ?? 'Unassigned'} ·{' '}
                    {a.word_count.toLocaleString()} words · {formatRelative(a.created_at)}
                  </div>
                </div>
                {a.status === 'pending' && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                      onClick={() => approveMut.mutate(a.id)}
                      disabled={approveMut.isPending}
                      className="bg-status-approved-accent text-white rounded px-2.5 py-1 text-xs hover:opacity-90 disabled:opacity-60"
                    >
                      {approveMut.isPending && approveMut.variables === a.id ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setRevisionFor(a)}
                      className="bg-transparent border border-border rounded px-2.5 py-1 text-xs text-ink-secondary hover:bg-surface-muted"
                    >
                      Request revision
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        <aside className="border border-border rounded-md bg-surface-card p-3 h-fit">
          <h2 className="text-sm font-medium mb-2">Webhook delivery log</h2>
          <p className="text-[11px] text-ink-tertiary mb-3">
            Each approval POSTs to the WordPress receiver. Most recent first.
          </p>
          <div className="flex flex-col gap-2">
            {deliveries.slice(0, 12).map((d) => {
              const ok = d.response_status >= 200 && d.response_status < 300
              return (
                <div key={d.id} className="border border-border rounded px-2 py-1.5 text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-[10px]',
                        ok
                          ? 'bg-status-approved-bg text-status-approved-fg'
                          : 'bg-status-danger-bg text-status-danger-fg',
                      )}
                    >
                      {d.response_status || 'err'}
                    </span>
                    <span className="text-ink-tertiary truncate">{d.id}</span>
                  </div>
                  <div className="text-ink-secondary mt-1 truncate">
                    {d.event} · {d.related_id}
                  </div>
                  <div className="text-ink-tertiary text-[10px] mt-0.5">{formatRelative(d.created_at)}</div>
                </div>
              )
            })}
            {deliveries.length === 0 && (
              <div className="text-xs text-ink-tertiary py-4 text-center">No deliveries yet.</div>
            )}
          </div>
        </aside>
      </div>

      {revisionFor && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
          onClick={() => setRevisionFor(null)}
        >
          <div
            className="bg-surface-card rounded-lg border border-border p-5 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-medium">Request revision</h3>
            <p className="text-xs text-ink-secondary mt-1 mb-3">
              Sends this piece back to draft. The writer gets your note.
            </p>
            <div className="text-sm mb-2 font-medium truncate">{revisionFor.article_title}</div>
            <textarea
              autoFocus
              value={revisionNote}
              onChange={(e) => setRevisionNote(e.target.value)}
              rows={4}
              placeholder="What needs to change?"
              className="w-full border border-border rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setRevisionFor(null)}
                className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-surface-muted"
              >
                Cancel
              </button>
              <button
                disabled={!revisionNote.trim() || revisionMut.isPending}
                onClick={() => revisionMut.mutate({ id: revisionFor.id, note: revisionNote.trim() })}
                className="px-3 py-1.5 text-xs bg-brand-600 text-white rounded-md hover:opacity-90 disabled:opacity-60"
              >
                {revisionMut.isPending ? 'Sending…' : 'Send back to draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
